import React, { useState, useEffect, useMemo, useCallback } from "react";
import { Armchair, Plus, Trash2, Users, UserPlus, GripVertical, RefreshCw } from "lucide-react";
import { useMember } from "../context/MemberContext";
import { fetchToolData, saveToolData, davetliKey } from "../lib/toolCloud";

const STORAGE_KEY = "dugun_oturma_v1";
const uid = () => Math.random().toString(36).slice(2, 9);

const readApproved = (mid) => {
  try {
    const raw = localStorage.getItem(davetliKey(mid));
    if (raw) {
      const d = JSON.parse(raw);
      return (d.guests || [])
        .filter((g) => g.status === "onayli")
        .map((g) => ({ id: g.id, name: g.name, count: Number(g.count) || 1, fromList: true }));
    }
  } catch {}
  return [];
};

const defaultTables = () => [
  { id: uid(), name: "Masa 1", seats: 8 },
  { id: uid(), name: "Masa 2", seats: 8 },
];

const SeatingTool = () => {
  const { token, member } = useMember();
  const mid = member?.id;
  const [tables, setTables] = useState(defaultTables);
  const [assignments, setAssignments] = useState({});
  const [manualGuests, setManualGuests] = useState([]);
  const [approved, setApproved] = useState([]);
  const [guestName, setGuestName] = useState("");
  const [tableName, setTableName] = useState("");
  const [tableSeats, setTableSeats] = useState(8);
  const [dragOver, setDragOver] = useState(null);
  const [cloudReady, setCloudReady] = useState(false);

  useEffect(() => {
    let active = true;
    setCloudReady(false);
    (async () => {
      let d = await fetchToolData("oturma", token);
      if (!d && mid) {
        try { const raw = localStorage.getItem(`${STORAGE_KEY}_${mid}`); if (raw) d = JSON.parse(raw); } catch {}
      }
      if (active) {
        setTables(Array.isArray(d?.tables) && d.tables.length ? d.tables : defaultTables());
        setAssignments(d?.assignments || {});
        setManualGuests(Array.isArray(d?.manualGuests) ? d.manualGuests : []);
        setApproved(readApproved(mid));
        setCloudReady(true);
      }
    })();
    return () => { active = false; };
  }, [token, mid]);

  useEffect(() => {
    if (!cloudReady || !mid) return;
    localStorage.setItem(`${STORAGE_KEY}_${mid}`, JSON.stringify({ tables, assignments, manualGuests }));
    if (token) saveToolData("oturma", token, { tables, assignments, manualGuests });
  }, [tables, assignments, manualGuests, cloudReady, token, mid]);

  const refresh = useCallback(() => setApproved(readApproved(mid)), [mid]);
  useEffect(() => {
    window.addEventListener("focus", refresh);
    window.addEventListener("storage", refresh);
    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("storage", refresh);
    };
  }, [refresh]);

  const allGuests = useMemo(
    () => [...approved, ...manualGuests.map((m) => ({ ...m, count: 1, fromList: false }))],
    [approved, manualGuests]
  );

  const move = (guestId, tableId) => setAssignments((a) => ({ ...a, [guestId]: tableId }));

  const addManual = (e) => {
    e.preventDefault();
    if (!guestName.trim()) return;
    setManualGuests((g) => [...g, { id: uid(), name: guestName.trim() }]);
    setGuestName("");
  };
  const removeManual = (id) => {
    setManualGuests((g) => g.filter((x) => x.id !== id));
    setAssignments((a) => { const n = { ...a }; delete n[id]; return n; });
  };

  const addTable = (e) => {
    e.preventDefault();
    if (!tableName.trim()) return;
    setTables((t) => [...t, { id: uid(), name: tableName.trim(), seats: Number(tableSeats) || 8 }]);
    setTableName("");
    setTableSeats(8);
  };
  const removeTable = (id) => {
    setAssignments((a) => {
      const n = { ...a };
      Object.keys(n).forEach((k) => { if (n[k] === id) n[k] = null; });
      return n;
    });
    setTables((t) => t.filter((x) => x.id !== id));
  };

  const onDrop = (e, tableId) => {
    e.preventDefault();
    setDragOver(null);
    const id = e.dataTransfer.getData("text/plain");
    if (id) move(id, tableId);
  };

  const unassigned = allGuests.filter((g) => !assignments[g.id]);
  const atTable = (id) => allGuests.filter((g) => assignments[g.id] === id);
  const peopleAt = (id) => atTable(id).reduce((s, g) => s + (Number(g.count) || 1), 0);
  const totalSeats = tables.reduce((s, t) => s + (Number(t.seats) || 0), 0);
  const totalPeople = allGuests.reduce((s, g) => s + (Number(g.count) || 1), 0);

  const GuestChip = ({ g }) => (
    <div
      draggable
      onDragStart={(e) => e.dataTransfer.setData("text/plain", g.id)}
      data-testid={`guest-chip-${g.id}`}
      className={`flex items-center gap-1.5 pl-2 pr-1 py-1.5 rounded-lg border text-sm text-[#2A2224] cursor-grab active:cursor-grabbing shadow-sm ${g.fromList ? "bg-[#FBF3E4] border-[#EBD9AF]" : "bg-white border-[#F0DDE0]"}`}
    >
      <GripVertical className="w-3.5 h-3.5 text-[#c9a0a6] shrink-0" />
      <span className="truncate">{g.name}{g.count > 1 ? ` (${g.count})` : ""}</span>
      <select
        value={assignments[g.id] || ""}
        onChange={(e) => move(g.id, e.target.value || null)}
        data-testid={`guest-move-${g.id}`}
        className="ml-1 text-xs bg-white/70 rounded px-1 py-0.5 outline-none text-[#7C6E70] max-w-[70px]"
      >
        <option value="">Havuz</option>
        {tables.map((t) => (
          <option key={t.id} value={t.id}>{t.name}</option>
        ))}
      </select>
      {!g.fromList && (
        <button onClick={() => removeManual(g.id)} data-testid={`guest-remove-${g.id}`} className="text-[#c9a0a6] hover:text-[#9E2A3B] p-0.5">
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );

  return (
    <div className="bg-white rounded-2xl border border-[#F0DDE0] p-6 sm:p-8" data-testid="seating-tool">
      <div className="flex items-center justify-between gap-3 mb-6">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#C15B6B] to-[#9E2A3B] flex items-center justify-center">
            <Armchair className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-display text-2xl font-bold text-[#2A2224]">Oturma Planı</h1>
            <p className="text-sm text-[#7C6E70]">Misafirleri masalara sürükleyip bırakarak yerleştirin</p>
          </div>
        </div>
        <button onClick={refresh} data-testid="seating-refresh" className="flex items-center gap-2 px-3 py-2 rounded-xl border border-[#F0DDE0] text-[#7C6E70] text-sm hover:bg-[#FBF5F0] transition-colors shrink-0">
          <RefreshCw className="w-4 h-4" /> <span className="hidden sm:inline">Yenile</span>
        </button>
      </div>

      {/* Link hint */}
      <div className="mb-5 flex items-start gap-2 rounded-xl bg-[#FBF3E4] border border-[#EBD9AF] px-4 py-3 text-sm text-[#8a6d1f]" data-testid="seating-link-hint">
        <Users className="w-4 h-4 mt-0.5 shrink-0" />
        <span>
          <b>Davetli Listem</b>'de <b>Onaylı</b> olarak işaretlediğiniz {approved.length} davetli buraya otomatik eklendi (sarı etiketli).
        </span>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3 mb-6">
        <div className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-3 text-center">
          <p className="text-xs text-[#7C6E70]">Kişi</p>
          <p className="text-xl font-bold text-[#2A2224]" data-testid="seating-guest-count">{totalPeople}</p>
        </div>
        <div className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-3 text-center">
          <p className="text-xs text-[#7C6E70]">Masa</p>
          <p className="text-xl font-bold text-[#2A2224]">{tables.length}</p>
        </div>
        <div className="rounded-xl border border-[#F0DDE0] bg-[#FBF5F0] p-3 text-center">
          <p className="text-xs text-[#7C6E70]">Toplam Koltuk</p>
          <p className="text-xl font-bold text-[#2A2224]">{totalSeats}</p>
        </div>
      </div>

      {/* Add forms */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
        <form onSubmit={addManual} className="flex gap-2">
          <input
            data-testid="seating-guest-input"
            value={guestName}
            onChange={(e) => setGuestName(e.target.value)}
            placeholder="Manuel misafir ekle..."
            className="flex-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
          />
          <button data-testid="seating-add-guest" type="submit" className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#9E2A3B] text-white text-sm font-medium hover:bg-[#861f2f] transition-colors">
            <UserPlus className="w-4 h-4" /> Misafir
          </button>
        </form>
        <form onSubmit={addTable} className="flex gap-2">
          <input
            data-testid="seating-table-input"
            value={tableName}
            onChange={(e) => setTableName(e.target.value)}
            placeholder="Masa adı..."
            className="flex-1 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
          />
          <input
            data-testid="seating-table-seats"
            type="number"
            min="1"
            value={tableSeats}
            onChange={(e) => setTableSeats(e.target.value)}
            className="w-20 px-3 py-2.5 rounded-xl border border-[#F0DDE0] text-sm focus:outline-none focus:border-[#9E2A3B]"
            title="Koltuk sayısı"
          />
          <button data-testid="seating-add-table" type="submit" className="flex items-center gap-1.5 px-4 py-2.5 rounded-xl bg-[#C6A15B] text-white text-sm font-medium hover:bg-[#b08e46] transition-colors">
            <Plus className="w-4 h-4" /> Masa
          </button>
        </form>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Unassigned pool */}
        <div
          onDragOver={(e) => { e.preventDefault(); setDragOver("pool"); }}
          onDragLeave={() => setDragOver(null)}
          onDrop={(e) => onDrop(e, null)}
          data-testid="seating-pool"
          className={`rounded-2xl border-2 border-dashed p-4 min-h-[180px] transition-colors ${dragOver === "pool" ? "border-[#9E2A3B] bg-[#FBF0F2]" : "border-[#F0DDE0] bg-[#FBF5F0]"}`}
        >
          <p className="flex items-center gap-1.5 text-sm font-semibold text-[#5b4d4f] mb-3">
            <Users className="w-4 h-4 text-[#C15B6B]" /> Atanmamış ({unassigned.length})
          </p>
          <div className="flex flex-col gap-2">
            {unassigned.map((g) => <GuestChip key={g.id} g={g} />)}
            {unassigned.length === 0 && <p className="text-xs text-[#9c8f90]">Tüm misafirler yerleştirildi 🎉</p>}
          </div>
        </div>

        {/* Tables */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {tables.map((t) => {
            const seated = atTable(t.id);
            const ppl = peopleAt(t.id);
            const full = ppl >= t.seats;
            return (
              <div
                key={t.id}
                onDragOver={(e) => { e.preventDefault(); setDragOver(t.id); }}
                onDragLeave={() => setDragOver(null)}
                onDrop={(e) => onDrop(e, t.id)}
                data-testid={`seating-table-${t.id}`}
                className={`rounded-2xl border-2 p-4 transition-colors ${dragOver === t.id ? "border-[#9E2A3B] bg-[#FBF0F2]" : "border-[#F0DDE0] bg-white"}`}
              >
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-[#FBF3E4] flex items-center justify-center">
                      <Armchair className="w-4 h-4 text-[#C6A15B]" />
                    </span>
                    <div>
                      <p className="text-sm font-semibold text-[#2A2224]">{t.name}</p>
                      <p className={`text-xs ${full ? "text-[#C0392B] font-medium" : "text-[#9c8f90]"}`}>{ppl}/{t.seats} kişi{full ? " · dolu" : ""}</p>
                    </div>
                  </div>
                  <button onClick={() => removeTable(t.id)} data-testid={`table-remove-${t.id}`} className="text-[#c9a0a6] hover:text-[#9E2A3B]">
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex flex-col gap-2 min-h-[40px]">
                  {seated.map((g) => <GuestChip key={g.id} g={g} />)}
                  {seated.length === 0 && <p className="text-xs text-[#c4b7b9] italic">Misafir sürükleyin</p>}
                </div>
              </div>
            );
          })}
          {tables.length === 0 && <p className="text-sm text-[#9c8f90]">Henüz masa yok. Yukarıdan ekleyin.</p>}
        </div>
      </div>
    </div>
  );
};

export default SeatingTool;
